import { useEffect, useMemo } from 'react';
import { NotificationController } from './NotificationController';
import { useNotifications } from './useNotifications';
import { selectScopeMarkers } from './extensions/NotificationScopeRegistry';
import type { NotificationMarker } from '../common/types';

/**
 * Непрочитанные уведомления одной области — то, что показывает бейдж раздела.
 *
 * Считается по сводке маркеров, а не по ленте: лента ограничена 50 последними
 * строками, и раздел с давними непрочитанными выглядел бы пустым.
 *
 * @param code Код области из `registerNotificationScope`
 *
 * @example
 * const { count, markRead } = useNotificationScope('grasias:contractor-docs');
 */
export function useNotificationScope(code: string): {
  /** Сколько объектов раздела ждут внимания */
  count: number;
  /** Маркеры области — например, чтобы подсветить строки в списке */
  markers: NotificationMarker[];
  /** Погасить всю область целиком */
  markRead: () => Promise<void>;
} {
  const markers = useNotifications(state => state.markers);
  const controller = NotificationController.getInstance();

  const scopeMarkers = useMemo(() => selectScopeMarkers(code, markers), [code, markers]);

  return useMemo(
    () => ({
      count: scopeMarkers.length,
      markers: scopeMarkers,
      // Гасим по идентификаторам, а не по ключам групп: область вправе узнавать
      // свои уведомления по entity_type/entity_url, и ключа у них может не быть
      markRead: () => controller.markReadByIds(scopeMarkers.map(marker => marker.id)),
    }),
    [controller, scopeMarkers]
  );
}

/**
 * Погасить уведомления по ключам групп, пока смонтирован компонент.
 *
 * Карточка объекта сообщает ключ своей группы, и уведомление уходит из бейджа
 * раздела — «прочитано» здесь означает «пользователь открыл сам объект», а не
 * «заглянул в список».
 *
 * Гашение намеренно **не** сверяется со сводкой перед первой попыткой: сводка
 * ограничена сверху, и объект за её границей иначе остался бы непрочитанным
 * навсегда. Запрос идемпотентен, а повтор при появлении нового маркера штатен —
 * событие, случившееся при открытой карточке, гасится сразу.
 *
 * @param dedupKeys Ключ или ключи; пустое значение — ничего не делает
 *
 * @example
 * useNotificationRead(contractorDocNotificationKey(queryGuid));
 */
export function useNotificationRead(dedupKeys: string | string[] | null | undefined): void {
  const markers = useNotifications(state => state.markers);
  const controller = NotificationController.getInstance();

  const keys = useMemo(() => normalizeKeys(dedupKeys), [dedupKeys]);
  const keysSignature = signKeys(keys);

  // Первая попытка — при открытии карточки и при смене объекта
  useEffect(() => {
    const target = parseKeys(keysSignature);
    if (target.length === 0) return;

    void controller.markReadByDedupKeys(target);
  }, [controller, keysSignature]);

  // Повторы — на события, пришедшие уже при открытой карточке
  const pendingSignature = useMemo(() => {
    const unread = new Set(collectDedupKeys(markers));
    return signKeys(keys.filter(key => unread.has(key)));
  }, [keys, markers]);

  useEffect(() => {
    const target = parseKeys(pendingSignature);
    if (target.length === 0) return;

    void controller.markReadByDedupKeys(target);
  }, [controller, pendingSignature]);
}

/** Ключи групп из маркеров; маркеры без ключа гасить нечем */
function collectDedupKeys(markers: NotificationMarker[]): string[] {
  const keys = markers
    .map(marker => marker.dedup_key)
    .filter((key): key is string => typeof key === 'string' && key.length > 0);

  return Array.from(new Set(keys));
}

/** Привести аргумент к списку непустых ключей */
function normalizeKeys(dedupKeys: string | string[] | null | undefined): string[] {
  if (!dedupKeys) return [];

  const list = Array.isArray(dedupKeys) ? dedupKeys : [dedupKeys];
  return list.filter(key => key.length > 0);
}

/**
 * Свернуть ключи в подпись для зависимостей эффекта.
 *
 * Список ключей пересоздаётся на каждый рендер и гонял бы эффект вхолостую;
 * подпись — стабильное представление того же содержимого. Формат JSON, а не
 * склейка разделителем: ограничений на символы в ключе контракт не даёт, и
 * ключ вида `tasks|42` при склейке распался бы на два чужих.
 */
function signKeys(keys: string[]): string {
  return JSON.stringify(keys);
}

/** Разобрать подпись обратно в ключи */
function parseKeys(signature: string): string[] {
  const parsed: unknown = JSON.parse(signature);
  return Array.isArray(parsed) ? parsed.filter(key => typeof key === 'string') : [];
}
